// ----------------------------------------------------------------------------
// AlertSettingsView — premium notification preferences page.
// Uses the Shadcn Switch primitive. Each toggle is independent local state for
// the demo; wire each row to a `PATCH /api/preferences` later.
// ----------------------------------------------------------------------------

import React, { useState } from "react";
import { Switch } from "../ui/switch";
import {
  Mail,
  MessageSquareText,
  Bell,
  Building2,
  AlarmClock,
  ShieldAlert,
  Globe2,
} from "lucide-react";
import { ALERTS } from "../../constants/testIds";

const SECTIONS = [
  {
    title: "Bid Discovery",
    description:
      "Alerts for new solicitations matching your monitored agencies and trades.",
    items: [
      {
        key: "brevard-email",
        icon: Mail,
        label: "Email notifications for new Brevard County bids",
        sub: "Includes Brevard County BoCC and Sheriff's Office postings.",
        defaultOn: true,
      },
      {
        key: "palmbay-email",
        icon: Building2,
        label: "City of Palm Bay daily watchlist",
        sub: "Sent at 7:00 AM ET Mon–Fri.",
        defaultOn: false,
      },
      {
        key: "weekly-sms",
        icon: MessageSquareText,
        label: "Weekly SMS digest summary",
        sub: "Sundays at 6:00 PM ET — top 10 closing bids.",
        defaultOn: true,
      },
    ],
  },
  {
    title: "Deadline Reminders",
    description: "Get reminded before key submission windows close.",
    items: [
      {
        key: "closing-72h",
        icon: AlarmClock,
        label: "72-hour deadline pre-alert",
        sub: "Push + email when a tracked bid enters the final 72 hours.",
        defaultOn: true,
      },
      {
        key: "closing-day",
        icon: Bell,
        label: "Day-of submission reminders",
        sub: "Final 4-hour cutoff alert for any saved contract.",
        defaultOn: true,
      },
    ],
  },
  {
    title: "Account & Compliance",
    description: "System notices that affect your vendor profile.",
    items: [
      {
        key: "bonding",
        icon: ShieldAlert,
        label: "Bonding capacity utilization warnings",
        sub: "Notify when active P&P bonds reach 80% of carrier limit.",
        defaultOn: true,
      },
      {
        key: "platform-changes",
        icon: Globe2,
        label: "Platform policy & posting updates",
        sub: "VendorLink, BonfireHub, and DemandStar system advisories.",
        defaultOn: false,
      },
    ],
  },
];

const ToggleRow = ({ item }) => {
  const [on, setOn] = useState(item.defaultOn);
  const Icon = item.icon;
  return (
    <div className="flex items-start gap-4 py-4 border-b border-slate-100 last:border-b-0">
      <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
        <Icon size={16} strokeWidth={1.7} className="text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">{item.label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{item.sub}</p>
      </div>
      <Switch
        data-testid={ALERTS.toggle(item.key)}
        checked={on}
        onCheckedChange={setOn}
        className="data-[state=checked]:bg-[#0c63e7]"
      />
    </div>
  );
};

export const AlertSettingsView = () => {
  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-sky-700 font-semibold">
          Preferences
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
          Alert Settings
        </h1>
        <p className="text-slate-500 text-sm sm:text-base max-w-2xl">
          Configure which procurement signals reach your team. Changes are saved
          automatically.
        </p>
      </header>

      <div className="space-y-6">
        {SECTIONS.map((sec) => (
          <section
            key={sec.title}
            className="bg-white border border-slate-200 rounded-lg shadow-sm"
          >
            <div className="px-6 pt-5 pb-4 border-b border-slate-100">
              <h2 className="font-display text-lg font-semibold text-slate-900">
                {sec.title}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">{sec.description}</p>
            </div>
            <div className="px-6">
              {sec.items.map((it) => (
                <ToggleRow key={it.key} item={it} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};
